import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";

import { StyledCard } from "../common/StyledCard";
import InfoRow from "../common/InfoRow";
import { TermResponseDto } from "../../shared/dtos/term/TermResponseDto";
import { formatDateForDisplay } from "../../helpers/dateHelpers";

interface TermCardProps {
  term: TermResponseDto;
  onEdit?: () => void;
  onDelete?: () => void;
}

const TermCard: React.FC<TermCardProps> = ({ term, onEdit, onDelete }) => {
  return (
    <StyledCard>
      <CardContent
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                flexWrap: "noWrap",
              }}
            >
              {term.name}
            </Typography>
            <Stack
              direction="row"
              sx={{
                spacing: 1,
                mt: 0.5,
              }}
            >
              {term.academicYearName && (
                <Chip
                  label={term.academicYearName}
                  size="small"
                  icon={<CalendarTodayIcon fontSize="small" />}
                  variant="outlined"
                  sx={{ height: 24, fontSize: "0.75rem" }}
                />
              )}
              {term.isCurrentTerm && (
                <Chip
                  label="Current"
                  size="small"
                  color="success"
                  icon={<CheckCircleOutlineIcon fontSize="small" />}
                  sx={{ height: 24, fontSize: "0.75rem" }}
                />
              )}
            </Stack>
          </Box>
          <Stack direction="row">
            {onEdit && (
              <Tooltip title="Edit Term">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete Term">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Details Body */}
        <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
          <InfoRow
            icon={<EventAvailableIcon fontSize="small" color="action" />}
            label="Start Date"
            value={formatDateForDisplay(term.startDate)}
          />
          <InfoRow
            icon={<EventBusyIcon fontSize="small" color="action" />}
            label="End Date"
            value={formatDateForDisplay(term.endDate)}
          />
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export default TermCard;
